/**
 * Notes Component
 *
 * Displays and manages user notes with filtering, search, and calendar functionality.
 * Provides comprehensive note management with team-based access control and geographical filtering.
 *
 * Key Features:
 * - Calendar view for date-based note filtering
 * - Team-based note filtering with public/private visibility
 * - Province/region-based geographical filtering (East/West regions + individual provinces)
 * - Exact visibility-level filtering (levels 1 through 4)
 * - Real-time search across note content, titles, authors, accounts, and contacts
 * - Integration with Google services (Calendar and Gmail)
 * - AI chat integration via event bus for note analysis and assistance
 * - Responsive design with mobile-friendly layout
 * - Persistent state management for dropdown selections and search text
 * - Accounts-style responsive controls with creation and empty-state recovery
 *
 * State Persistence:
 * - Province filter selection persisted to localStorage as 'notes-province-filter'
 * - Team filter selection persisted to localStorage as 'notes-team-filter'
 * - Level filter selection persisted to localStorage as 'notes-level-filter'
 * - Search query persisted to localStorage as 'notes-search-query'
 * - Filter and search state automatically restored on page load/refresh
 *
 * Updated: Added localStorage persistence for dropdown selections and search text to maintain user preferences across sessions.
 * The button is disabled when no notes are available and uses the notes summary prompt utility.
 * Recent change: Adopted the shared page layout to remove duplicate shell spacing.
 * Recent change: Centered the Notes heading against the adjacent New note button.
 * Recent change: Notes fetches independently of team metadata and trusts response-owned province data instead of issuing per-account hydration requests.
 * Recent change: The calendar remains available through a compact disclosure below the desktop breakpoint.
 * Recent change: Uses server-side filters and stable 40-row pages so note history growth does not block initial rendering.
 * Recent change: Refreshes calendar metadata only when its month/filter scope changes or a note mutation occurs.
 * Recent change: Retrieves complete long-note content before composing Calendar, Gmail, or per-note AI actions.
 * Recent change: Adds an exact server-side minimum visibility-level filter to the Notes toolbar, count, and calendar metadata.
 */

// IMPORTANT: Do not remove ID attributes from HTML elements in this file.
// These IDs are used for testing, styling, and DOM manipulation purposes.

import { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useEventBus } from '../context/EventBusContext';
import { useChatContext } from '../context/ChatContext';
import { THEME_CLASSES } from '../constants/themeConstants';
import { Note, PROVINCES, normalizeProvinceValue } from '../models/Note';
import Spinner from '../components/common/Spinner';
import PageLayout from '../components/patterns/PageLayout';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { useTeams } from '../context/TeamContext';
import { getResponsiveGridClassesWithTransitions } from '../utils/responsiveUtils';
import { highlightText } from '../utils/highlightUtils';
import { openNoteInCalendar, openNoteInGmail } from '../utils/googleUtils';
import { getNotesSummaryPrompt } from '../utils/summaryUtils';
import NewsBadge from '../components/note/NewsBadge';
import TasksBadge from '../components/note/TasksBadge';
import { EVENTS, type NoteDragToChatEventData } from '../constants/eventConstants';
import { Avatar, Button, Surface } from '../components/primitives';
import { getNoteTypeBadgeClassName } from '../components/note/noteTypeBadgeUtils';

// Define a proper type for the team structure
interface Team {
  id?: string;
  teamId?: string;
  name?: string;
  teamName?: string;
  [key: string]: any; // Allow other properties
}

type ProvinceValue = typeof PROVINCES[number]['value'];
type ProvinceFilterValue = ProvinceValue | 'east' | 'west' | '';
const NOTE_LEVELS = [1, 2, 3, 4] as const;
type NoteLevel = typeof NOTE_LEVELS[number];

const NOTES_PAGE_SIZE = 40;
const NOTES_SEARCH_DEBOUNCE_MS = 300;

type NoteCursor = {
  beforeUpdatedAt: string;
  beforeId: number;
  beforeAssociationType: number;
  beforeAssociationId: number;
};

type CalendarBucket = {
  noteDate: string | Date;
  noteCount: number;
};

const toLocalDateParameter = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeProvinceFilterValue = (province?: string | null): ProvinceFilterValue => {
  if (!province) {
    return '';
  }

  if (province === 'east' || province === 'west') {
    return province;
  }

  return normalizeProvinceValue(province) ?? '';
};

const getInitialProvinceFilter = (): ProvinceFilterValue => {
  const storedProvince = localStorage.getItem('notes-province-filter');
  return normalizeProvinceFilterValue(storedProvince);
};

const normalizeLevelFilterValue = (level?: string | null): NoteLevel | null => {
  const parsedLevel = Number(level);
  return NOTE_LEVELS.includes(parsedLevel as NoteLevel) ? parsedLevel as NoteLevel : null;
};

const getInitialLevelFilter = (): NoteLevel | null => {
  return normalizeLevelFilterValue(localStorage.getItem('notes-level-filter'));
};

const Notes: FC = () => {
  const { publish, subscribe, unsubscribe } = useEventBus();
  const { teams, isLoading: isTeamsLoading } = useTeams();
  const { isChatVisible } = useChatContext();
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<ProvinceFilterValue>(getInitialProvinceFilter);
  const [selectedLevel, setSelectedLevel] = useState<NoteLevel | null>(getInitialLevelFilter);
  const [selectedTeam, setSelectedTeam] = useState<string>(() => {
    return localStorage.getItem('notes-team-filter') || '';
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem('notes-search-query') || '';
  });
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedInitialPage, setHasLoadedInitialPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<NoteCursor | null>(null);
  const [calendarCounts, setCalendarCounts] = useState<Record<string, number>>({});
  const [calendarRefreshVersion, setCalendarRefreshVersion] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [draggingNoteId, setDraggingNoteId] = useState<number | null>(null);
  const requestGenerationRef = useRef(0);
  const calendarRequestRef = useRef(0);
  const lastRequestedFilterKeyRef = useRef<string | null>(null);

  const listFilterParams = useMemo(() => ({
    search: debouncedSearchQuery.trim() || null,
    teamId: selectedTeam ? Number(selectedTeam) : null,
    province: selectedProvince || null,
    minimumLevel: selectedLevel,
    date: selectedDate ? toLocalDateParameter(selectedDate) : null,
    newsOnly: false,
  }), [debouncedSearchQuery, selectedDate, selectedLevel, selectedProvince, selectedTeam]);

  const filterKey = useMemo(() => JSON.stringify(listFilterParams), [listFilterParams]);

  const fetchCount = useCallback(async (generation: number) => {
    try {
      const result = await request(API_ENDPOINTS.GET_USER_NOTES_COUNT, listFilterParams);
      if (generation === requestGenerationRef.current) {
        setTotalCount(Number(result?.count ?? 0));
      }
    } catch (error) {
      console.warn('Failed to fetch Notes count:', error);
      if (generation === requestGenerationRef.current) {
        setTotalCount(null);
      }
    }
  }, [listFilterParams]);

  const fetchFirstPage = useCallback(async (force = false) => {
    if (!force && lastRequestedFilterKeyRef.current === filterKey) {
      return;
    }

    lastRequestedFilterKeyRef.current = filterKey;
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    setIsLoading(true);
    setIsLoadingMore(false);
    setLoadError(null);
    setLoadMoreError(null);
    setTotalCount(null);
    setHasMore(false);
    setNextCursor(null);

    try {
      const page = await request(API_ENDPOINTS.GET_USER_NOTES_PAGE, {
        ...listFilterParams,
        limit: NOTES_PAGE_SIZE,
        beforeUpdatedAt: null,
        beforeId: null,
        beforeAssociationType: null,
        beforeAssociationId: null,
      }) as Note[];

      if (generation !== requestGenerationRef.current) {
        return;
      }

      const nextNotes = Array.isArray(page) ? page : [];
      const lastNote = nextNotes.at(-1);
      setNotes(nextNotes);
      setHasLoadedInitialPage(true);
      setHasMore(Boolean(nextNotes[0]?.hasMore));
      setNextCursor(lastNote?.sortUpdatedAt && lastNote.id && lastNote.associationType !== undefined && lastNote.associationId !== undefined
        ? {
          beforeUpdatedAt: new Date(lastNote.sortUpdatedAt).toISOString(),
          beforeId: lastNote.id,
          beforeAssociationType: lastNote.associationType,
          beforeAssociationId: lastNote.associationId,
        }
        : null);
      void fetchCount(generation);
    } catch (error) {
      if (generation !== requestGenerationRef.current) {
        return;
      }
      console.error('Failed to fetch notes:', error);
      setNotes([]);
      setHasLoadedInitialPage(true);
      setLoadError('Unable to load notes right now.');
    } finally {
      if (generation === requestGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchCount, filterKey, listFilterParams]);

  const fetchNextPage = useCallback(async () => {
    if (!hasMore || !nextCursor || isLoadingMore) {
      return;
    }

    const generation = requestGenerationRef.current;
    setIsLoadingMore(true);
    setLoadMoreError(null);

    try {
      const page = await request(API_ENDPOINTS.GET_USER_NOTES_PAGE, {
        ...listFilterParams,
        limit: NOTES_PAGE_SIZE,
        ...nextCursor,
      }) as Note[];

      if (generation !== requestGenerationRef.current) {
        return;
      }

      const nextNotes = Array.isArray(page) ? page : [];
      const lastNote = nextNotes.at(-1);
      setNotes((currentNotes) => [...currentNotes, ...nextNotes]);
      setHasMore(Boolean(nextNotes[0]?.hasMore));
      setNextCursor(lastNote?.sortUpdatedAt && lastNote.id && lastNote.associationType !== undefined && lastNote.associationId !== undefined
        ? {
          beforeUpdatedAt: new Date(lastNote.sortUpdatedAt).toISOString(),
          beforeId: lastNote.id,
          beforeAssociationType: lastNote.associationType,
          beforeAssociationId: lastNote.associationId,
        }
        : null);
    } catch (error) {
      if (generation === requestGenerationRef.current) {
        console.error('Failed to fetch more notes:', error);
        setLoadMoreError('Unable to load more notes right now.');
      }
    } finally {
      if (generation === requestGenerationRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [hasMore, isLoadingMore, listFilterParams, nextCursor]);

  // Extract unique teams with proper type safety
  const availableTeams = useMemo(() => {
    // Use a more defensive approach to handle teams
    const typedTeams = Array.isArray(teams) ? (teams as unknown as Team[]) : [];

    // Create array with teams from context
    const teamsArray = typedTeams.map(team => ({
      id: String(team.teamId || team.id || ''),  // Convert to string to ensure consistency
      name: team.name || team.teamName || `Team ${team.teamId || team.id}`
    }))
      .filter(team => team.id); // Filter out any team without an ID

    return teamsArray;
  }, [teams]);

  useEffect(() => {
    if (isTeamsLoading || !selectedTeam) {
      return;
    }

    if (!availableTeams.some((team) => team.id === selectedTeam)) {
      setSelectedTeam('');
    }
  }, [availableTeams, isTeamsLoading, selectedTeam]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, NOTES_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    void fetchFirstPage();
  }, [fetchFirstPage]);

  useEffect(() => {
    const handleNotesUpdated = () => {
      setCalendarRefreshVersion((version) => version + 1);
      void fetchFirstPage(true);
    };

    subscribe(EVENTS.NOTES_UPDATED, handleNotesUpdated);

    return () => {
      unsubscribe(EVENTS.NOTES_UPDATED, handleNotesUpdated);
    };
  }, [fetchFirstPage, subscribe, unsubscribe]);

  useEffect(() => {
    if (!hasLoadedInitialPage) {
      return;
    }

    const generation = requestGenerationRef.current;
    const calendarRequest = calendarRequestRef.current + 1;
    calendarRequestRef.current = calendarRequest;
    const month = toLocalDateParameter(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1));

    const fetchCalendarCounts = async () => {
      try {
        const result = await request(API_ENDPOINTS.GET_USER_NOTES_CALENDAR, {
          search: listFilterParams.search,
          teamId: listFilterParams.teamId,
          province: listFilterParams.province,
          minimumLevel: listFilterParams.minimumLevel,
          date: null,
          newsOnly: false,
          month,
        }) as CalendarBucket[];

        if (generation !== requestGenerationRef.current || calendarRequest !== calendarRequestRef.current) {
          return;
        }

        setCalendarCounts(Object.fromEntries(
          (Array.isArray(result) ? result : []).map((bucket) => [
            typeof bucket.noteDate === 'string' ? bucket.noteDate.slice(0, 10) : toLocalDateParameter(new Date(bucket.noteDate)),
            Number(bucket.noteCount),
          ])
        ));
      } catch (error) {
        console.warn('Failed to fetch Notes calendar summary:', error);
        if (generation === requestGenerationRef.current && calendarRequest === calendarRequestRef.current) {
          setCalendarCounts({});
        }
      }
    };

    void fetchCalendarCounts();
  }, [calendarRefreshVersion, currentMonth, hasLoadedInitialPage, listFilterParams.minimumLevel, listFilterParams.province, listFilterParams.search, listFilterParams.teamId]);

  // Persist dropdown selections and search query to localStorage
  useEffect(() => {
    localStorage.setItem('notes-province-filter', selectedProvince);
  }, [selectedProvince]);

  useEffect(() => {
    localStorage.setItem('notes-team-filter', selectedTeam);
  }, [selectedTeam]);

  useEffect(() => {
    localStorage.setItem('notes-level-filter', selectedLevel?.toString() ?? '');
  }, [selectedLevel]);

  useEffect(() => {
    localStorage.setItem('notes-search-query', searchQuery);
  }, [searchQuery]);

  const keywords = useMemo(() =>
    searchQuery
      .toLowerCase()
      .split(' ')
      .map(k => k.trim())
      .filter(k => k.length > 0),
    [searchQuery]
  );

  const getCalendarDates = () => {
    const startDate = new Date(currentMonth);
    startDate.setDate(1);
    const startDay = startDate.getDay();
    startDate.setDate(startDate.getDate() - startDay);

    // Get the last day of the month
    const endDate = new Date(currentMonth);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);

    // Calculate if we need 6 weeks
    const lastDayPosition = startDay + endDate.getDate() - 1;
    const needsSixWeeks = lastDayPosition > 34;
    const daysToShow = needsSixWeeks ? 42 : 35;

    const dates = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const hasActiveNoteFilters = Boolean(searchQuery || selectedProvince || selectedLevel || selectedTeam || selectedDate);

  const clearNoteFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedProvince('');
    setSelectedLevel(null);
    setSelectedTeam('');
    setSelectedDate(null);
  }, []);

  const openNewNote = useCallback(() => {
    publish(EVENTS.OPEN_NOTE_MODAL, {});
  }, [publish]);

  const loadCompleteNote = useCallback(async (note: Note): Promise<Note> => {
    if (!note.contentTruncated) {
      return note;
    }

    const completeNote = await request(API_ENDPOINTS.GET_NOTE, { id: note.id });
    if (!completeNote) {
      throw new Error(`Complete note ${note.id} was not found.`);
    }

    return { ...note, ...completeNote, contentTruncated: false };
  }, []);

  const runCompleteNoteAction = useCallback((
    note: Note,
    action: (completeNote: Note, targetWindow: Window | null) => void
  ) => {
    if (!note.contentTruncated) {
      action(note, null);
      return;
    }

    const targetWindow = window.open('', '_blank');
    void loadCompleteNote(note)
      .then((completeNote) => action(completeNote, targetWindow))
      .catch((error) => {
        targetWindow?.close();
        console.error('Failed to load complete note for external action:', error);
      });
  }, [loadCompleteNote]);

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, note: Note) => {
    // Prepare drag data
    const dragData: NoteDragToChatEventData = {
      noteId: note.id,
      title: note.title || note.content?.split('\n')[0] || 'Untitled Note',
      content: note.contentTruncated
        ? `${note.content || ''}\n\n[Preview truncated. Open the note to view its complete content.]`
        : note.content || '',
      author: note.author || 'Unknown User',
      type: note.type || 'general',
      accountName: note.accountName,
      contactName: note.contactName,
      updatedAt: note.updatedAt instanceof Date ? note.updatedAt.toISOString() : new Date(note.updatedAt).toISOString()
    };

    // Set drag data
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';

    // Set dragging state for visual feedback
    setDraggingNoteId(note.id);

    // Publish event to signal that chat should be ready for drop
    publish(EVENTS.CHAT_OPEN_FOR_DROP, {});
  };

  const handleDragEnd = () => {
    // Clear dragging state
    setDraggingNoteId(null);
  };

  const isPageLoading = isLoading && !hasLoadedInitialPage;

  if (isPageLoading) return (
    <div className="fixed inset-0 flex items-center justify-center" id="notes-loading">
      <Spinner />
    </div>
  );

  const dates = getCalendarDates();

  // Calculate responsive grid classes based on chat visibility
  const gridClasses = getResponsiveGridClassesWithTransitions('notes', isChatVisible);

  return (
    <PageLayout className="overflow-hidden px-4" id="notes-page-container">
      {/* Page Header */}
      <div className="mx-4 flex flex-col gap-3 sm:mx-6 xl:flex-row xl:items-center xl:justify-between">
        {/* Left section - Notes heading and creation */}
        <div className="flex flex-wrap items-center gap-3 xl:shrink-0 xl:flex-nowrap">
          <h1 className={`m-0 flex h-9 items-center ${THEME_CLASSES.text.primary} text-xl font-bold tracking-tight`}>Notes</h1>
          <Button
            onClick={openNewNote}
            size="sm"
            className="min-h-9"
            aria-label="New note"
            leftIcon={(
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 3a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H4a1 1 0 1 1 0-2h5V4a1 1 0 0 1 1-1z" clipRule="evenodd" />
              </svg>
            )}
          >
            <span className="xl:hidden 2xl:inline">New note</span>
          </Button>
        </div>

        {/* Middle section - Team, province, and visibility-level filters */}
        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 xl:w-auto xl:shrink-0 xl:grid-cols-[10rem_10rem_10rem] 2xl:grid-cols-[11rem_11rem_11rem]">
          {/* Team Filter */}
          <select
            value={selectedTeam}
            id="notes-team-filter"
            aria-label="Filter notes by team"
            onChange={(e) => setSelectedTeam(e.target.value)}
            className={`w-full pl-3 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500
              ${THEME_CLASSES.input.border}
              ${THEME_CLASSES.input.background}
              ${THEME_CLASSES.input.text}`}
          >
            <option value="">All Teams</option>
            {availableTeams.map(team => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>

          {/* Visibility Level Filter */}
          <select
            value={selectedLevel ?? ''}
            id="notes-level-filter"
            aria-label="Filter notes by level"
            onChange={(e) => setSelectedLevel(normalizeLevelFilterValue(e.target.value))}
            className={`w-full pl-3 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500
              ${THEME_CLASSES.input.border}
              ${THEME_CLASSES.input.background}
              ${THEME_CLASSES.input.text}`}
          >
            <option value="">All Levels</option>
            {NOTE_LEVELS.map((level) => (
              <option key={level} value={level}>
                Level {level}
              </option>
            ))}
          </select>

          {/* Province Filter */}
          <select
            value={selectedProvince}
            id="notes-province-filter"
            aria-label="Filter notes by province"
            onChange={(e) => setSelectedProvince(e.target.value as ProvinceFilterValue)}
            className={`w-full pl-3 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500
              ${THEME_CLASSES.input.border}
              ${THEME_CLASSES.input.background}
              ${THEME_CLASSES.input.text}`}
          >
            <option value="">All Provinces</option>
            <option value="east">East Region</option>
            <option value="west">West Region</option>
            {PROVINCES.map(province => (
              <option key={province.value} value={province.value}>
                {province.label}
              </option>
            ))}
          </select>

        </div>

        {/* Right section - Note count, AI action, and search */}
        <div className="relative grid w-full grid-cols-[auto_1fr] items-center gap-2 sm:grid-cols-[auto_auto_minmax(0,1fr)] xl:w-auto xl:shrink-0 xl:grid-cols-[auto_auto_13rem] 2xl:grid-cols-[auto_auto_15rem]">
          {/* Note count */}
          <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300" aria-live="polite">
            {totalCount ?? `${notes.length}${hasMore ? '+' : ''}`} {(totalCount ?? notes.length) === 1 ? 'note' : 'notes'}
          </span>

          {/* Chat button */}
          <Button
            onClick={() => {
              if (notes.length === 0) return;
              const prompt = `Summarize only these ${notes.length} loaded note previews. Do not imply this is the user's complete note history.\n\n${getNotesSummaryPrompt(notes)}`;
              publish('chat:open', prompt);
            }}
            variant="icon"
            className="h-9 w-9 justify-self-start"
            title="Ask AI about loaded note previews"
            aria-label="Ask AI about loaded note previews"
            disabled={notes.length === 0}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </Button>

          {/* Search box */}
          <div className="relative col-span-2 w-full sm:col-span-1">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              id="notes-search-input"
              placeholder="Search notes..."
              aria-label="Search notes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-11 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                aria-label="Clear note search"
              >
                <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary} hover:text-gray-900 dark:hover:text-gray-100`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 1 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414l-4.293 4.293 4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col lg:flex-row gap-4 overflow-hidden">
        {/* Calendar */}
        <div className="w-full flex-shrink-0 lg:w-1/4 lg:max-w-[300px]" id="notes-calendar-section">
          <Button
            type="button"
            variant="secondary"
            className="flex w-full items-center justify-between lg:hidden"
            aria-expanded={isCalendarExpanded}
            aria-controls="notes-calendar-panel"
            onClick={() => setIsCalendarExpanded((expanded) => !expanded)}
          >
            <span>Calendar</span>
            <svg
              className={`h-4 w-4 transition-transform ${isCalendarExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Button>

          <div
            id="notes-calendar-panel"
            className={`${isCalendarExpanded ? 'block' : 'hidden'} mt-3 lg:mt-0 lg:block`}
          >
            {/* Calendar Header */}
            <div className="mb-3 flex items-center justify-between lg:mb-4" id="notes-calendar-nav">
              <Button
                onClick={() => {
                  const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
                  setCurrentMonth(newDate);
                }}
                variant="icon"
                className="h-8 w-8"
                aria-label="Previous month"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Button>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100" id="calendar-current-month">
                {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
              <Button
                onClick={() => {
                  const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
                  setCurrentMonth(newDate);
                }}
                variant="icon"
                className="h-8 w-8"
                aria-label="Next month"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>

            {/* Calendar Grid */}
            <Surface variant="elevated" className="mb-4 overflow-y-auto p-3 sm:p-4" id="notes-calendar-grid">
              <div className="mb-3 grid grid-cols-7 gap-1 sm:mb-4">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className={`py-1 text-center text-sm font-bold sm:py-2 sm:text-base ${THEME_CLASSES.text.primary}`}>
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {dates.map((date) => {
                const dayNoteCount = calendarCounts[toLocalDateParameter(date)] ?? 0;

                const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                const isToday = date.toDateString() === new Date().toDateString();

                return (
                  <div key={date.toISOString()} className="flex aspect-square min-w-0 items-center justify-center">
                    <button
                      onClick={() => {
                        if (selectedDate?.toDateString() === date.toDateString()) {
                          setSelectedDate(null);
                        } else {
                          setSelectedDate(date);
                        }
                      }}
                      className={`relative flex h-full w-full max-h-10 max-w-10 items-center justify-center rounded-full text-sm
                        ${isToday ? 'font-bold' : ''}
                        ${isCurrentMonth
                          ? 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                          : 'opacity-50'
                        }
                        ${selectedDate?.toDateString() === date.toDateString()
                          ? 'ring-2 ring-blue-500 rounded-full'
                          : ''
                        }
                      `}
                      aria-label={`${selectedDate?.toDateString() === date.toDateString() ? 'Clear' : 'Select'} ${date.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                      aria-pressed={selectedDate?.toDateString() === date.toDateString()}
                    >
                      {date.getDate()}
                      {dayNoteCount > 0 && (
                        <span className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-500" />
                      )}
                    </button>
                  </div>
                );
                })}
              </div>
            </Surface>
          </div>
        </div>

        {/* Notes */}
        <div className="flex-1 flex flex-col overflow-hidden" id="notes-content-section">
          {/* Notes Content */}
          <div className="flex-1 overflow-y-auto p-4 relative">
            {loadError ? (
              <Surface variant="elevated" className="mx-auto max-w-xl px-6 py-12 text-center" id="notes-error-state">
                <h2 className={`${THEME_CLASSES.text.primary} text-lg font-semibold`}>Unable to load notes</h2>
                <p className={`mt-3 text-sm ${THEME_CLASSES.text.secondary}`}>{loadError}</p>
                <div className="mt-5">
                  <Button onClick={() => void fetchFirstPage(true)} size="sm">
                    Retry
                  </Button>
                </div>
              </Surface>
            ) : notes.length === 0 ? (
              <Surface variant="elevated" className="mx-auto max-w-xl px-6 py-12 text-center" id="notes-empty-state">
                <h2 className={`${THEME_CLASSES.text.primary} text-lg font-semibold`}>
                  {hasActiveNoteFilters ? 'No notes match your filters' : 'No notes yet'}
                </h2>
                <p className={`mt-3 text-sm ${THEME_CLASSES.text.secondary}`}>
                  {hasActiveNoteFilters
                    ? 'Clear the active filters to see all of your notes.'
                    : 'Create a note to keep important account and contact details close at hand.'}
                </p>
                <div className="mt-5">
                  <Button onClick={hasActiveNoteFilters ? clearNoteFilters : openNewNote} size="sm">
                    {hasActiveNoteFilters ? 'Clear filters' : 'New note'}
                  </Button>
                </div>
              </Surface>
            ) : (
              <div className={gridClasses} id="notes-grid">
                {isLoading ? (
                // Loading skeleton cards
                [...Array(6)].map((_, index) => (
                  <div key={`skeleton-${index}`} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border dark:border-gray-700 flex flex-col h-full animate-pulse">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                      <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                    <div className="w-3/4 h-5 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                    <div className="space-y-2">
                      <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                ))
                ) : (
                  notes.map((item) => {
                    const author = item.author || 'Unknown User';
                    const typeBadgeClassName = getNoteTypeBadgeClassName(item.type);

                    return (
                      <Surface
                        key={`${item.id}-${item.accountId || ''}-${item.contactId || ''}-${item.type || ''}`}
                        variant="interactive"
                        className={`p-6 flex flex-col h-full cursor-pointer shadow-lg ${draggingNoteId === item.id
                          ? 'opacity-60 scale-95 cursor-grabbing'
                          : 'hover:cursor-grab'
                          } transition-all duration-200 ease-in-out`}
                        id={`note-card-${item.id}`}
                        onClick={() => {
                          if (item.accountId) {
                            window.location.href = `/accounts/${item.accountId}/notes/${item.id}`;
                          } else if (item.contactId) {
                            window.location.href = `/contacts/${item.contactId}/notes/${item.id}`;
                          }
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar name={author} size="sm" className="bg-red-400" />
                            <div className={`${THEME_CLASSES.text.primary} text-sm font-medium`}>
                              {highlightText(author, keywords)}
                            </div>
                          </div>
                          <div className={`${THEME_CLASSES.text.secondary} text-xs`}>
                            {new Date(item.updatedAt).toLocaleDateString()}
                          </div>
                        </div>

                      {(item.accountId || item.contactId) && (
                        <div className="flex items-center justify-between mb-2">
                          <div className={`text-xs ${THEME_CLASSES.text.secondary}`}>
                            {item.accountId ? (
                              <>Account: {highlightText(item.accountName || `#${item.accountId}`, keywords)}</>
                            ) : item.contactId ? (
                              <>Contact: {highlightText(item.contactName || `#${item.contactId}`, keywords)}</>
                            ) : null}
                          </div>
                        </div>
                      )}

                      {(item.title || item.content) && (
                        <h3 className={`${THEME_CLASSES.text.primary} font-medium mb-1 whitespace-nowrap overflow-hidden text-ellipsis`}>
                          {highlightText(item.title || item.content?.split('\n')[0] || '', keywords)}
                        </h3>
                      )}

                      <p className={`${THEME_CLASSES.text.secondary} text-sm mt-1 line-clamp-3 flex-1`}>
                        {highlightText(
                          item.content
                            ? item.content
                              .split('\n')
                              .slice(1)
                              .map(line => line.trim())
                              .join('\n')
                              .trim()
                            : '',
                          keywords
                        )}
                      </p>

                      {/* Type badge and action links */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          {typeBadgeClassName && (
                            <span className={`px-2 py-0.5 text-xs rounded-full ${typeBadgeClassName}`}>
                              {highlightText(item.type, keywords)}
                            </span>
                          )}

                          <NewsBadge note={item} />
                          <TasksBadge note={item} />
                        </div>

                        <div className="flex gap-2 ml-auto">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              const startDate = new Date();
                              startDate.setMinutes(0, 0, 0);
                              startDate.setHours(startDate.getHours() + 1);
                              runCompleteNoteAction(item, (completeNote, targetWindow) => {
                                openNoteInCalendar(completeNote, startDate, undefined, undefined, targetWindow);
                              });
                            }}
                            variant="icon"
                            className="h-8 w-8"
                            title="Add to Google Calendar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              runCompleteNoteAction(item, (completeNote, targetWindow) => {
                                openNoteInGmail(completeNote, '', undefined, undefined, targetWindow);
                              });
                            }}
                            variant="icon"
                            className="h-8 w-8"
                            title="Compose Gmail"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              void loadCompleteNote(item)
                                .then((completeNote) => publish('chat:open', getNotesSummaryPrompt([completeNote])))
                                .catch((error) => console.error('Failed to load complete note for AI:', error));
                            }}
                            variant="icon"
                            className="h-8 w-8"
                            title="Ask AI about this complete note"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                      </Surface>
                    );
                  })
                )}
              </div>
            )}
            {!loadError && notes.length > 0 && (hasMore || loadMoreError) && (
              <div className="flex flex-col items-center gap-2 py-5" id="notes-load-more">
                {loadMoreError && <p className="text-sm text-red-600 dark:text-red-400">{loadMoreError}</p>}
                <Button onClick={() => void fetchNextPage()} size="sm" disabled={isLoadingMore}>
                  {isLoadingMore ? 'Loading more…' : loadMoreError ? 'Retry loading more' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Notes;
